/**
 * Closed allowlist of handoff destinations.
 *
 * Exact path-plus-query strings, not a path prefix: the three settings
 * destinations differ *only* by their `?tab=`, so a path-only check would
 * validate nothing that distinguishes them and would leave `tab` free.
 *
 * Adding a destination here is one of three edits. The other two are the
 * `target` to `next` map in `shopify-app/app/routes/app.handoff.tsx` and the
 * link itself.
 */
export const ALLOWED_NEXT_DESTINATIONS: readonly string[] = [
  '/dashboard/settings?tab=templates',
  '/dashboard/settings?tab=plan',
  '/dashboard/settings?tab=booking-page',
  '/dashboard/home',
]

export const DEFAULT_NEXT_DESTINATION = '/dashboard/home'

const MAX_DECODE_PASSES = 3

/**
 * Percent-decoding is repeated so a double-encoded value cannot smuggle a
 * different string past the comparison. Bounded, because a crafted input can
 * otherwise be decoded forever.
 */
function decodeFully(value: string): string {
  let current = value
  for (let pass = 0; pass < MAX_DECODE_PASSES; pass += 1) {
    let decoded: string
    try {
      decoded = decodeURIComponent(current)
    } catch {
      return current
    }
    if (decoded === current) return current
    current = decoded
  }
  return current
}

/**
 * Resolves the requested destination against the allowlist.
 *
 * Returns the matched **allowlist constant**, never the caller's own string, so
 * nothing the caller wrote can reach `window.location`. Anything not on the
 * list, including an absolute URL, a protocol-relative `//host`, a
 * backslash-prefixed value or an allowed path with a different query, silently
 * becomes the account home page: this route has just minted a session, so an
 * unvalidated redirect here is an open redirect with a live credential
 * attached.
 */
export function resolveNextDestination(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_NEXT_DESTINATION

  const normalized = decodeFully(raw).trim()
  const match = ALLOWED_NEXT_DESTINATIONS.find((allowed) => allowed === normalized)

  return match ?? DEFAULT_NEXT_DESTINATION
}
